/**
 * AsteroidField — texture-atlas loader + object pool for asteroid sprites.
 *
 * The art ships as a single sheet (Asteroids_Sheet.png → keyed to a transparent
 * background as asteroids.png) containing many differently shaped/coloured rocks
 * scattered irregularly. `asteroidFrames.ts` holds the auto-detected bounding
 * box of each rock. This class:
 *
 *   1. ASSET LOADER   — loads the sheet once (Assets.load).
 *   2. FRAME DEFINITION — slices it into individual Textures via sub-frames.
 *   3. POOLING         — recycles Sprite instances instead of allocating per
 *                        asteroid (there can be hundreds across warps).
 *   4. RANDOMIZATION   — randomFrame() picks a rock at spawn time.
 *
 * `sync()` reconciles the live sprite set with the current asteroid data each
 * frame; `releaseAll()` returns every sprite to the pool on a warp.
 */
import { Assets, Container, Rectangle, Sprite, Texture } from "pixi.js";
import type { Asteroid } from "@/game/world/procedural";
import { ASTEROID_FRAMES } from "./asteroidFrames";

export class AsteroidField {
  private container = new Container();
  private frames: Texture[] = [];
  private pool: Sprite[] = []; // free (hidden) sprites ready for reuse
  private active = new Map<string, Sprite>(); // asteroidId → live sprite

  /** The display object to add to the world layer. */
  get view(): Container {
    return this.container;
  }

  // 1 + 2 — load the sheet and define one Texture per detected frame.
  async load(): Promise<void> {
    const base = import.meta.env.BASE_URL;
    const sheet: Texture = await Assets.load(`${base}sprites/asteroids.png`);
    this.frames = ASTEROID_FRAMES.map(
      (f) =>
        new Texture({
          source: sheet.source,
          frame: new Rectangle(f.x, f.y, f.w, f.h),
        }),
    );
  }

  // 4 — random frame selection.
  randomFrame(): Texture {
    return this.frames[(Math.random() * this.frames.length) | 0]!;
  }

  // 3 — pool acquire/release.
  private acquire(): Sprite {
    const s = this.pool.pop() ?? new Sprite();
    s.visible = true;
    this.container.addChild(s);
    return s;
  }

  private release(s: Sprite): void {
    s.visible = false;
    this.container.removeChild(s);
    this.pool.push(s);
  }

  /**
   * Reconcile sprites with the current asteroid set. New asteroids get a pooled
   * sprite + random frame (sized to the asteroid's radius); vanished ones are
   * released; depleted rocks fade out.
   */
  sync(asteroids: Asteroid[]): void {
    const present = new Set<string>();
    for (const a of asteroids) {
      present.add(a.id);
      let s = this.active.get(a.id);
      if (!s) {
        s = this.acquire();
        const tex = this.randomFrame();
        s.texture = tex;
        s.anchor.set(0.5);
        // Scale so the rock's longest side ≈ its gameplay diameter (2·radius).
        s.scale.set((a.radius * 2) / Math.max(tex.width, tex.height));
        s.rotation = Math.random() * Math.PI * 2; // vary orientation
        s.position.set(a.pos.x, a.pos.y);
        this.active.set(a.id, s);
      }
      s.alpha = a.ore > 0 ? 1 : 0.35; // fade depleted rocks
    }
    // Release sprites for asteroids that no longer exist.
    for (const [id, s] of this.active) {
      if (!present.has(id)) {
        this.release(s);
        this.active.delete(id);
      }
    }
  }

  /** Return every active sprite to the pool (call on warp before re-syncing). */
  releaseAll(): void {
    for (const s of this.active.values()) this.release(s);
    this.active.clear();
  }
}
